// CompanyEventsScreen.js — Full-featured company events with real API integration
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, FlatList,
  Image, Modal, TextInput, ScrollView, ActivityIndicator, Alert,
  Switch, Share, Dimensions, RefreshControl, KeyboardAvoidingView,
  Platform, Animated,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';

const { width, height } = Dimensions.get('window');

// ── API ───────────────────────────────────────────────────────────────────────
import EventsService from '../../../services/events.service'; // uses uploaded events_service.js

// Lightweight helper for write operations not covered by the uploaded EventsService
const apiRequest = async (path, method = 'GET', body = null) => {
  const token = await AsyncStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw Object.assign(new Error(e.message || `HTTP ${res.status}`), e); }
  return res.json();
};

// Direct register call — the uploaded EventsService doesn't expose this endpoint
const registerViaAPI = async (eventId, professionalId) => {
  const token = await AsyncStorage.getItem('auth_token');
  const res = await fetch(`${API_BASE}/api/events/${eventId}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ professional_id: parseInt(professionalId) }),
  });
  const data = await res.json();
  if (data.success) return { success: true, registrationCode: data.registrationCode ?? null, duplicate: false };
  if (data.errorCode === 'DUPLICATE_REGISTRATION') return { success: false, registrationCode: null, duplicate: true };
  // Fallback to data API
  await apiRequest('/api/data/event_registrations', 'POST', {
    event_id: eventId, professional_id: parseInt(professionalId),
    status: 'attending', registered_at: new Date().toISOString(),
  });
  return { success: true, registrationCode: null, duplicate: false };
};

// Keep API_BASE/API_KEY for the direct fetch in handleRsvp (register endpoint returns registrationCode)
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ── Constants ─────────────────────────────────────────────────────────────────
const TODAY     = new Date().toISOString().split('T')[0];
const EVENT_TYPES = ['Conference','Workshop','Training','Networking','Retreat','Meeting','Social','Webinar','Other'];
const EVENT_COLORS = {
  Conference: '#0e4e96', Workshop: '#7B1FA2', Training: '#D32F2F',
  Networking: '#F57C00', Retreat: '#388E3C',  Meeting: '#455A64',
  Social:     '#C2185B', Webinar:  '#0097A7',  Other: '#5D4037',
};
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const fmtTime  = d => d ? new Date(d).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : '';
const fmtShort = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '—';
const typeColor = t => EVENT_COLORS[t] || '#455A64';
const initials  = s => (s || '').split(' ').slice(0,2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

const getDaysUntil = (dateStr) => {
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const getCapacityPct = (registered, max) => {
  if (!max) return 0;
  return Math.min(100, Math.round((registered / max) * 100));
};

// ── Sub-components ─────────────────────────────────────────────────────────────
const TypeBadge = ({ type, small }) => (
  <View style={{ backgroundColor: typeColor(type), borderRadius: 6,
    paddingHorizontal: small ? 6 : 9, paddingVertical: small ? 2 : 4 }}>
    <Text style={{ color: '#fff', fontSize: small ? 10 : 12, fontWeight: '700' }}>{type}</Text>
  </View>
);

const StatusBadge = ({ event }) => {
  const now = new Date();
  const start = new Date(event.start_date);
  const end   = new Date(event.end_date);
  let label, bg, fg;
  if (now < start) {
    const d = getDaysUntil(event.start_date);
    if (d === 0)       { label = 'TODAY'; bg = '#E3F2FD'; fg = '#0e4e96'; }
    else if (d === 1)  { label = 'TOMORROW'; bg = '#FFF3E0'; fg = '#E65100'; }
    else if (d <= 7)   { label = `${d}d left`; bg = '#F3E5F5'; fg = '#6A1B9A'; }
    else               { label = 'Upcoming'; bg = '#E8F5E9'; fg = '#2E7D32'; }
  } else if (now <= end) {
    label = 'LIVE'; bg = '#FFEBEE'; fg = '#C62828';
  } else {
    label = 'Past'; bg = '#F5F5F5'; fg = '#757575';
  }
  return (
    <View style={{ backgroundColor: bg, borderRadius: 8, paddingHorizontal: 7, paddingVertical: 3 }}>
      <Text style={{ fontSize: 10, fontWeight: '800', color: fg, letterSpacing: 0.4 }}>{label}</Text>
    </View>
  );
};

const CapacityBar = ({ registered, max, color }) => {
  const pct = getCapacityPct(registered, max);
  const barColor = pct >= 90 ? '#D32F2F' : pct >= 70 ? '#F57C00' : color || '#0e4e96';
  return (
    <View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text style={{ fontSize: 11, color: '#78909C' }}>Capacity</Text>
        <Text style={{ fontSize: 11, fontWeight: '700', color: barColor }}>{registered}/{max} ({pct}%)</Text>
      </View>
      <View style={{ height: 5, backgroundColor: '#ECEFF1', borderRadius: 3, overflow: 'hidden' }}>
        <View style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 3 }} />
      </View>
    </View>
  );
};

const SectionTitle = ({ title, icon }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12, marginTop: 20 }}>
    {icon && <Ionicons name={icon} size={17} color="#0e4e96" />}
    <Text style={{ fontSize: 15, fontWeight: '800', color: '#0F172A', textTransform: 'uppercase',
      letterSpacing: 0.6, flex: 1, borderBottomWidth: 2, borderBottomColor: '#E3F2FD', paddingBottom: 4 }}>
      {title}
    </Text>
  </View>
);

// ── MAIN SCREEN ───────────────────────────────────────────────────────────────
export default function CompanyEventsScreen({ navigation }) {
  const [companyId,     setCompanyId]     = useState(1);
  const [companyName,   setCompanyName]   = useState('');
  const [companyLogo,   setCompanyLogo]   = useState(null);
  const [userId,        setUserId]        = useState(null);
  const [userName,      setUserName]      = useState('You');

  // Data
  const [events,        setEvents]        = useState([]);
  const [filteredEvents,setFilteredEvents]= useState([]);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [saving,        setSaving]        = useState(false);

  // UI state
  const [activeTab,     setActiveTab]     = useState('Upcoming');
  const [viewMode,      setViewMode]      = useState('list');     // list | grid
  const [searchQuery,   setSearchQuery]   = useState('');
  const [sortBy,        setSortBy]        = useState('date');
  const [sortOrder,     setSortOrder]     = useState('asc');
  const [showFilters,   setShowFilters]   = useState(false);
  const [filterType,    setFilterType]    = useState('All');
  const [filterCategory,setFilterCategory]= useState('All');

  // Modals
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showCreate,    setShowCreate]    = useState(false);
  const [showRsvp,      setShowRsvp]      = useState(null);      // event object
  const [showNotifs,    setShowNotifs]    = useState(false);
  const [showStats,     setShowStats]     = useState(false);
  const [showCalendar,  setShowCalendar]  = useState(false);
  const [detailTab,     setDetailTab]     = useState('about');   // about | agenda | speakers | attendees | logistics

  // RSVP
  const [rsvpMap,       setRsvpMap]       = useState({});         // eventId → 'attending'|'declined'|'waitlist'

  // Notifications
  const [notifications, setNotifications] = useState([]);

  // Calendar state
  const [calMonth,      setCalMonth]      = useState(new Date().getMonth());
  const [calYear,       setCalYear]       = useState(new Date().getFullYear());

  // Create form
  const [form, setForm] = useState(blankForm());
  function blankForm() {
    return {
      title: '', description: '', location_name: '', location_city: '', location_address: '',
      event_type: 'Meeting', organizer_name: '', organizer_email: '',
      start_date: TODAY + ' 09:00', end_date: TODAY + ' 17:00',
      registration_deadline: TODAY,
      max_attendees: '', registration_fee: '0', currency: 'GBP',
      is_public: false, registration_required: false, is_virtual: false,
      virtual_link: '', contact_email: '', contact_phone: '',
      agenda: [], speakers: [], requirements: [], tags: '',
      catering_notes: '', parking_notes: '', accessibility_notes: '',
      hashtag: '',
    };
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      AsyncStorage.getItem('company_id'),
      AsyncStorage.getItem('user_id'),
      AsyncStorage.getItem('user_name'),
      AsyncStorage.getItem('company_profile'),
    ]).then(([cid, uid, uname, cpRaw]) => {
      if (cid) setCompanyId(Number(cid));
      if (uid) setUserId(uid);
      if (uname) setUserName(uname);
      if (cpRaw) {
        try {
          const cp = JSON.parse(cpRaw);
          if (cp.company_name) setCompanyName(cp.company_name);
          const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
          if (logo) setCompanyLogo(logo);
        } catch {}
      }
    });

    const loadSaved = async () => {
      const saved = await AsyncStorage.getItem('events_rsvp').catch(() => null);
      if (saved) setRsvpMap(JSON.parse(saved));
      const savedNotifs = await AsyncStorage.getItem('events_notifs').catch(() => null);
      if (savedNotifs) setNotifications(JSON.parse(savedNotifs));
    };
    loadSaved();
  }, []);

  useEffect(() => {
    if (!companyId) return;
    fetchEvents();
    fetchCompanyProfile();
  }, [companyId]);

  const fetchCompanyProfile = async () => {
    try {
      const r = await apiRequest(`/api/data/company_profiles?company_id=${companyId}`);
      const list = Array.isArray(r) ? r : (r.data ?? []);
      const cp = (Array.isArray(list) ? list[0] : null) ?? r.data ?? r;
      if (cp && typeof cp === 'object') {
        if (cp.company_name) setCompanyName(cp.company_name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
        AsyncStorage.setItem('company_profile', JSON.stringify(cp)).catch(() => {});
      }
    } catch {}
  };

  // ── Fetch events ────────────────────────────────────────────────────────────
  const fetchEvents = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // EventsService.getEvents hits GET /api/data/company_events?company_id=X
      // It returns [] on any error (never throws), so no try/catch needed here.
      // `registered_count` is pre-mapped from `current_attendees` by formatEvents().
      // `is_full` is pre-calculated from max_attendees vs current_attendees.
      const eventsData = await EventsService.getEvents(companyId);
      setEvents(eventsData);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => { setRefreshing(true); fetchEvents(true); };

  // ── Filtering / sorting ────────────────────────────────────────────────────
  useEffect(() => {
    let f = [...events];
    const now = new Date();

    if (activeTab === 'Upcoming') f = f.filter(e => new Date(e.start_date) > now || new Date(e.end_date) > now);
    else if (activeTab === 'Past')      f = f.filter(e => new Date(e.end_date) < now);
    else if (activeTab === 'My Events') f = f.filter(e => rsvpMap[e.id] === 'attending');
    else if (activeTab === 'Today')     f = f.filter(e => new Date(e.start_date).toDateString() === now.toDateString());

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      f = f.filter(e => [e.title, e.description, e.location_name, e.location_city,
        e.organizer_name, e.event_type, ...(e.tags || [])]
        .some(v => (v || '').toLowerCase().includes(q)));
    }
    if (filterType !== 'All') f = f.filter(e => e.event_type === filterType);
    if (filterCategory !== 'All') f = f.filter(e => e.category === filterCategory);

    f.sort((a, b) => {
      let diff = 0;
      if (sortBy === 'date') diff = new Date(a.start_date) - new Date(b.start_date);
      else if (sortBy === 'title') diff = a.title.localeCompare(b.title);
      else if (sortBy === 'attendees') diff = (b.registered_count || 0) - (a.registered_count || 0);
      return sortOrder === 'asc' ? diff : -diff;
    });
    setFilteredEvents(f);
  }, [events, activeTab, searchQuery, filterType, filterCategory, sortBy, sortOrder, rsvpMap]);

  // ── Registration / RSVP ────────────────────────────────────────────────────
  // Uses POST /api/events/:id/register with professional_id
  // Handles registrationCode, DUPLICATE_REGISTRATION, waitlist, and cancellation
  const handleRsvp = async (event, status) => {
    setSaving(true);
    try {
      const profId = professionalId
        || parseInt(await AsyncStorage.getItem('professional_id').catch(() => null))
        || 4; // default fallback per API docs

      if (status === 'attending' || status === 'waitlist') {
        // ── Register via EventsService (handles primary + fallback) ────────
        let registrationCode = null;
        if (status === 'waitlist') {
          // joinWaitlist not on uploaded service — call data API directly
          await apiRequest(`/api/data/event_registrations`, 'POST', {
            event_id: event.id, professional_id: profId, status: 'waitlist',
            registered_at: new Date().toISOString(),
          });
        } else {
          // register not on uploaded service — use dedicated endpoint directly
          const result = await registerViaAPI(event.id, profId);
          if (result.duplicate) {
            setSaving(false);
            setShowRsvp(null);
            const updated = { ...rsvpMap, [event.id]: 'attending' };
            setRsvpMap(updated);
            AsyncStorage.setItem('events_rsvp', JSON.stringify(updated)).catch(() => {});
            Alert.alert('Already Registered', 'You are already registered for this event.', [{ text: 'OK' }]);
            return;
          }
          registrationCode = result.registrationCode;
        }

        // ── Persist state ─────────────────────────────────────────────────
        const updatedRsvp = { ...rsvpMap, [event.id]: status };
        setRsvpMap(updatedRsvp);
        await AsyncStorage.setItem('events_rsvp', JSON.stringify(updatedRsvp));

        if (registrationCode) {
          const updatedCodes = { ...regCodes, [event.id]: registrationCode };
          setRegCodes(updatedCodes);
          await AsyncStorage.setItem('events_reg_codes', JSON.stringify(updatedCodes));
        }

        // Refresh single event so registered_count reflects current_attendees from DB
        // (the uploaded service maps current_attendees → registered_count in formatEvents)
        EventsService.getEventById(event.id).then(updated => {
          if (updated) {
            setEvents(prev => prev.map(e => e.id === event.id ? { ...e, ...updated } : e));
          }
        }).catch(() => {
          // Optimistic fallback
          setEvents(prev => prev.map(e => e.id === event.id
            ? { ...e, registered_count: (e.registered_count || 0) + 1 } : e));
        });

        addNotification(
          status === 'attending' ? 'Registration Confirmed' : 'Added to Waitlist',
          `You registered for "${event.title}"${registrationCode ? ` · Code: ${registrationCode}` : ''}`,
          'success'
        );
        setShowRsvp(null);
        Alert.alert(
          status === 'attending' ? '✅ Registration Confirmed!' : '⏳ Added to Waitlist',
          status === 'attending'
            ? `You're registered for "${event.title}".${
                registrationCode
                  ? `\n\nYour registration code:\n${registrationCode}`
                  : "\nWe'll send you a reminder before the event."
              }`
            : "You've been added to the waitlist. We'll notify you if a spot opens.",
          [{ text: 'OK' }]
        );

      } else {
        // ── Cancel via EventsService ────────────────────────────────────
        // cancelRegistration not on uploaded service — call data API directly
        await apiRequest(`/api/events/${event.id}/register`, 'DELETE', { professional_id: profId })
          .catch(() => apiRequest('/api/data/event_registrations', 'POST', {
            event_id: event.id, professional_id: profId, status: 'cancelled',
          }));

        const updatedRsvp = { ...rsvpMap, [event.id]: 'declined' };
        setRsvpMap(updatedRsvp);
        await AsyncStorage.setItem('events_rsvp', JSON.stringify(updatedRsvp));

        // Remove saved code
        const updatedCodes = { ...regCodes };
        delete updatedCodes[event.id];
        setRegCodes(updatedCodes);
        await AsyncStorage.setItem('events_reg_codes', JSON.stringify(updatedCodes));

        // Refresh from server to get accurate current_attendees after cancellation
        EventsService.getEventById(event.id).then(updated => {
          if (updated) {
            setEvents(prev => prev.map(e => e.id === event.id ? { ...e, ...updated } : e));
          }
        }).catch(() => {
          if (rsvpMap[event.id] === 'attending') {
            setEvents(prev => prev.map(e => e.id === event.id
              ? { ...e, registered_count: Math.max(0, (e.registered_count || 1) - 1) } : e));
          }
        });

        addNotification('Registration Cancelled', `You cancelled your registration for "${event.title}"`, 'info');
        setShowRsvp(null);
        Alert.alert('Cancelled', 'Your registration has been cancelled.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      Alert.alert(
        'Registration Failed',
        err?.message === 'Network request failed'
          ? 'Could not connect to server. Please check your connection.'
          : err?.message || 'Could not register. Please try again.'
      );
    } finally { setSaving(false); }
  };
  // ── Create event ───────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.title.trim()) { Alert.alert('Required', 'Event title is required.'); return; }
    if (!form.location_name.trim()) { Alert.alert('Required', 'Location is required.'); return; }
    setSaving(true);
    try {
      // Generate event_id — required NOT NULL column in company_events table
      const event_id = `EVT-${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;

      // Only send fields the API table actually has — log so 500 is diagnosable
      const payload = {
        event_id,
        company_id:            parseInt(companyId),
        title:                 form.title.trim(),
        description:           form.description.trim() || undefined,
        event_type:            form.event_type,
        start_date:            form.start_date,
        end_date:              form.end_date,
        location_name:         form.location_name.trim() || undefined,
        location_city:         form.location_city.trim() || undefined,
        organizer_name:        form.organizer_name.trim() || undefined,
        max_attendees:         form.max_attendees ? parseInt(form.max_attendees) : undefined,
        registration_required: form.registration_required,
        registration_deadline: form.registration_required && form.registration_deadline
                                 ? form.registration_deadline : undefined,
        status:                'upcoming',
      };

      // Remove every key whose value is undefined
      const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([, v]) => v !== undefined)
      );

      console.log('Creating event payload:', JSON.stringify(cleanPayload, null, 2));

      const result = await apiRequest('/api/data/company_events', 'POST', cleanPayload);
      console.log('Create event result:', JSON.stringify(result));
      setShowCreate(false);
      setForm(blankForm());
      await fetchEvents(true);
      Alert.alert('Created', `"${payload.title}" has been created successfully.`);
    } catch (e) {
      console.error('Create event error full:', JSON.stringify(e), e?.message, e?.status);
      // Show the server's actual error detail when available
      const serverMsg = e?.error || e?.details || e?.detail || e?.message;
      Alert.alert(
        'Could Not Create Event',
        serverMsg || `Server error ${e?.status || ''}. Check logs for details.`,
        [{ text: 'OK' }]
      );
    } finally { setSaving(false); }
  };

  // ── Delete event ───────────────────────────────────────────────────────────
  const handleDelete = (event) => {
    Alert.alert('Delete Event', `Delete "${event.title}"? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          await apiRequest(`/api/data/company_events/${event.id}`, 'DELETE');
          setShowDetail(false);
          await fetchEvents(true);
        } catch { Alert.alert('Error', 'Could not delete event.'); }
      }},
    ]);
  };

  // ── Notifications ──────────────────────────────────────────────────────────
  const addNotification = (title, message, type = 'info') => {
    const n = { id: Date.now(), title, message, type, timestamp: new Date().toISOString(), read: false };
    setNotifications(prev => {
      const updated = [n, ...prev].slice(0, 50);
      AsyncStorage.setItem('events_notifs', JSON.stringify(updated)).catch(() => {});
      return updated;
    });
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));

  // ── Share event ────────────────────────────────────────────────────────────
  const shareEvent = (event) => {
    Share.share({
      message: `📅 ${event.title}\n📍 ${event.location_name}${event.location_city ? ', ' + event.location_city : ''}\n🗓 ${fmtDate(event.start_date)} at ${fmtTime(event.start_date)}\n${event.virtual_link || ''}`,
      title: event.title,
    });
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = {
    total:      events.length,
    upcoming:   events.filter(e => new Date(e.start_date) > new Date()).length,
    myRsvps:    Object.values(rsvpMap).filter(v => v === 'attending').length,
    totalPax:   events.reduce((s, e) => s + (e.registered_count || 0), 0),
    avgCapacity:events.filter(e => e.max_attendees > 0).reduce((s, e, _, a) =>
      s + getCapacityPct(e.registered_count, e.max_attendees) / a.length, 0) | 0,
    byType:     EVENT_TYPES.reduce((acc, t) => {
      acc[t] = events.filter(e => e.event_type === t).length; return acc; }, {}),
  };

  // ── Calendar helpers ───────────────────────────────────────────────────────
  const getCalendarDays = () => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const eventsOnDay = (day) => {
    if (!day) return [];
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => e.start_date && e.start_date.startsWith(dateStr));
  };

  // ── Render: Event Card ─────────────────────────────────────────────────────
  const renderEventCard = ({ item: e }) => {
    const myRsvp   = rsvpMap[e.id];
    const isFull   = e.is_full || (e.max_attendees && e.registered_count >= e.max_attendees);
    const color    = typeColor(e.event_type);
    const daysLeft = getDaysUntil(e.start_date);

    if (viewMode === 'grid') {
      return (
        <TouchableOpacity style={[S.gridCard, { borderTopColor: color, borderTopWidth: 3 }]}
          onPress={() => { setSelectedEvent(e); setDetailTab('about'); setShowDetail(true); }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
            <TypeBadge type={e.event_type} small />
            <StatusBadge event={e} />
          </View>
          <Text style={S.gridTitle} numberOfLines={2}>{e.title}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 6 }}>
            <Ionicons name="calendar-outline" size={11} color="#78909C" />
            <Text style={{ fontSize: 11, color: '#78909C' }}>{fmtShort(e.start_date)}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <Ionicons name="location-outline" size={11} color="#78909C" />
            <Text style={{ fontSize: 11, color: '#78909C' }} numberOfLines={1}>{e.location_name}</Text>
          </View>
          {e.max_attendees > 0 && (
            <View style={{ marginTop: 8 }}>
              <CapacityBar registered={e.registered_count} max={e.max_attendees} color={color} />
            </View>
          )}
          {myRsvp === 'attending' && (
            <View style={{ marginTop: 8, backgroundColor: '#E8F5E9', borderRadius: 8,
              paddingHorizontal: 8, paddingVertical: 3, alignSelf: 'flex-start' }}>
              <Text style={{ fontSize: 10, color: '#2E7D32', fontWeight: '700' }}>✓ GOING</Text>
            </View>
          )}
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity style={S.eventCard}
        onPress={() => { setSelectedEvent(e); setDetailTab('about'); setShowDetail(true); }}>
        {/* Banner image or colour strip */}
        {e.banner_image_url
          ? <Image source={{ uri: e.banner_image_url }} style={S.cardBanner} />
          : <LinearGradient colors={[color, color + 'BB']} style={S.cardBannerGrad}>
              <Text style={{ color: '#fff', fontSize: 32, opacity: 0.2, fontWeight: '900' }}>{e.event_type[0]}</Text>
            </LinearGradient>}

        <View style={S.cardBody}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', flex: 1 }}>
              <TypeBadge type={e.event_type} />
              {e.is_virtual && (
                <View style={{ backgroundColor: '#E3F2FD', borderRadius: 6, paddingHorizontal: 7, paddingVertical: 3 }}>
                  <Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '700' }}>VIRTUAL</Text>
                </View>
              )}
            </View>
            <StatusBadge event={e} />
          </View>

          <Text style={S.cardTitle}>{e.title}</Text>

          <View style={{ gap: 4, marginTop: 6 }}>
            <View style={S.cardMeta}>
              <Ionicons name="calendar-outline" size={13} color="#78909C" />
              <Text style={S.cardMetaText}>{fmtDate(e.start_date)} · {fmtTime(e.start_date)}</Text>
            </View>
            <View style={S.cardMeta}>
              <Ionicons name="location-outline" size={13} color="#78909C" />
              <Text style={S.cardMetaText} numberOfLines={1}>
                {e.location_name}{e.location_city ? `, ${e.location_city}` : ''}
              </Text>
            </View>
            {e.organizer_name ? (
              <View style={S.cardMeta}>
                <Ionicons name="person-outline" size={13} color="#78909C" />
                <Text style={S.cardMetaText}>By {e.organizer_name}</Text>
              </View>
            ) : null}
          </View>

          {e.max_attendees > 0 && (
            <View style={{ marginTop: 10 }}>
              <CapacityBar registered={e.registered_count} max={e.max_attendees} color={color} />
            </View>
          )}

          {e.description ? (
            <Text style={{ fontSize: 12, color: '#546E7A', marginTop: 8, lineHeight: 17 }} numberOfLines={2}>
              {e.description}
            </Text>
          ) : null}

          {/* Tags */}
          {e.tags?.length > 0 && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
              {e.tags.slice(0,4).map((t, i) => (
                <View key={i} style={S.tag}><Text style={S.tagText}>{t}</Text></View>
              ))}
            </View>
          )}

          {/* Footer actions */}
          <View style={S.cardFooter}>
            <View style={{ flexDirection: 'row', gap: 6, flex: 1 }}>
              {myRsvp === 'attending' ? (
                <TouchableOpacity
                  style={{ backgroundColor: '#E8F5E9', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6,
                    flexDirection: 'row', alignItems: 'center', gap: 4 }}
                  onPress={() => regCodes[e.id] && Alert.alert('Registration Code', `Your code for "${e.title}":\n\n${regCodes[e.id]}`)}>
                  <Ionicons name="checkmark-circle" size={13} color="#2E7D32" />
                  <Text style={{ fontSize: 12, color: '#2E7D32', fontWeight: '700' }}>Going</Text>
                  {regCodes[e.id] && (
                    <Text style={{ fontSize: 10, color: '#7B1FA2', fontWeight: '800' }}>🎟</Text>
                  )}
                </TouchableOpacity>
              ) : myRsvp === 'waitlist' ? (
                <View style={{ backgroundColor: '#FFF3E0', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 }}>
                  <Text style={{ fontSize: 12, color: '#E65100', fontWeight: '700' }}>⏳ Waitlisted</Text>
                </View>
              ) : e.registration_required && new Date() < new Date(e.start_date) ? (
                <TouchableOpacity style={[S.rsvpBtn, { backgroundColor: isFull ? '#F5F5F5' : color }]}
                  onPress={() => isFull ? setShowRsvp(e) : setShowRsvp(e)}>
                  <Text style={[S.rsvpBtnText, { color: isFull ? '#9E9E9E' : '#fff' }]}>
                    <Text>{isFull ? 'Join Waitlist' : 'RSVP'}</Text>
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity onPress={() => shareEvent(e)}
                style={{ padding: 6 }}>
                <Ionicons name="share-outline" size={17} color="#78909C" />
              </TouchableOpacity>
              {e.is_virtual && e.virtual_link ? (
                <TouchableOpacity
                  onPress={() => navigation?.navigate('BusinessVideoConferenceScreen', {
                    meetingTitle: e.title, autoJoin: false,
                  })}
                  style={{ padding: 6 }}>
                  <Ionicons name="videocam-outline" size={17} color={color} />
                </TouchableOpacity>
              ) : null}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // ── Render: Detail Modal ───────────────────────────────────────────────────
  const renderDetailModal = () => {
    const e = selectedEvent;
    if (!e) return null;
    const color  = typeColor(e.event_type);
    const myRsvp = rsvpMap[e.id];
    const isFull = e.is_full || (e.max_attendees && e.registered_count >= e.max_attendees);
    const DTABS  = ['about','agenda','speakers','logistics','attendees'];

    return (
      <Modal visible={showDetail} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
          {/* Header */}
          <View style={S.mHeader}>
            <TouchableOpacity onPress={() => setShowDetail(false)}>
              <Ionicons name="close" size={24} color="#607D8B" />
            </TouchableOpacity>
            <Text style={S.mHeaderTitle} numberOfLines={1}>{e.title}</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity onPress={() => shareEvent(e)}>
                <Ionicons name="share-outline" size={22} color="#0e4e96" />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => handleDelete(e)}>
                <Ionicons name="trash-outline" size={22} color="#D32F2F" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Hero */}
          {e.banner_image_url
            ? <Image source={{ uri: e.banner_image_url }} style={{ width: '100%', height: 180 }} resizeMode="cover" />
            : <LinearGradient colors={[color, color + '99']} style={{ height: 150, padding: 20, justifyContent: 'flex-end' }}>
                <TypeBadge type={e.event_type} />
                <Text style={{ color: '#fff', fontSize: 22, fontWeight: '900', marginTop: 8 }}>{e.title}</Text>
              </LinearGradient>}

          {/* Event title if has banner */}
          {e.banner_image_url && (
            <View style={{ padding: 16, paddingBottom: 0 }}>
              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
                <TypeBadge type={e.event_type} />
                {e.is_virtual && (
                  <View style={{ backgroundColor: '#E3F2FD', borderRadius: 6, paddingHorizontal: 7, paddingVertical: 3 }}>
                    <Text style={{ fontSize: 10, color: '#0e4e96', fontWeight: '700' }}>VIRTUAL</Text>
                  </View>
                )}
                <StatusBadge event={e} />
              </View>
              <Text style={{ fontSize: 20, fontWeight: '900', color: '#0F172A' }}>{e.title}</Text>
            </View>
          )}

          {/* Key info strip */}
          <View style={S.infoStrip}>
            <View style={S.infoStripItem}>
              <Ionicons name="calendar-outline" size={14} color="#0e4e96" />
              <Text style={S.infoStripText}>{fmtDate(e.start_date)}</Text>
            </View>
            <View style={S.infoStripItem}>
              <Ionicons name="time-outline" size={14} color="#0e4e96" />
              <Text style={S.infoStripText}>{fmtTime(e.start_date)}–{fmtTime(e.end_date)}</Text>
            </View>
            <View style={S.infoStripItem}>
              <Ionicons name="people-outline" size={14} color="#0e4e96" />
              <Text style={S.infoStripText}>{e.registered_count}{e.max_attendees ? `/${e.max_attendees}` : ''}</Text>
            </View>
          </View>

          {/* Sub-tabs */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false}
            style={{ backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', maxHeight: 44 }}
            contentContainerStyle={{ paddingHorizontal: 12 }}>
            {DTABS.map(t => (
              <TouchableOpacity key={t} style={[S.dTab, detailTab === t && { borderBottomWidth: 2, borderBottomColor: color }]}
                onPress={() => setDetailTab(t)}>
                <Text style={[S.dTabText, detailTab === t && { color, fontWeight: '800' }]}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 120 }}>
            {/* ── ABOUT ── */}
            {detailTab === 'about' && (
              <View>
                {/* Key details */}
                <View style={S.detailCard}>
                  {[
                    { icon: 'calendar-outline',    label: 'Date',         value: `${fmtDate(e.start_date)}${e.end_date !== e.start_date ? ' – ' + fmtDate(e.end_date) : ''}` },
                    { icon: 'time-outline',         label: 'Time',         value: `${fmtTime(e.start_date)} – ${fmtTime(e.end_date)}` },
                    { icon: 'location-outline',     label: 'Venue',        value: [e.location_name, e.location_city, e.location_address].filter(Boolean).join(', ') },
                    { icon: 'person-outline',       label: 'Organiser',    value: e.organizer_name },
                    { icon: 'mail-outline',         label: 'Contact',      value: e.contact_email || e.organizer_email },
                    { icon: 'call-outline',         label: 'Phone',        value: e.contact_phone },
                    { icon: 'calendar-lock-outline',label: 'Reg. Deadline',value: e.registration_deadline ? fmtDate(e.registration_deadline) : null },
                    { icon: 'cash-outline',         label: 'Fee',          value: e.registration_fee > 0 ? `${e.currency} ${e.registration_fee}` : 'Free' },
                    { icon: 'eye-outline',          label: 'Visibility',   value: e.is_public ? 'Public' : 'Internal' },
                  ].filter(r => r.value).map(r => (
                    <View key={r.label} style={S.detailRow}>
                      <View style={S.detailRowIcon}><Ionicons name={r.icon} size={15} color="#0e4e96" /></View>
                      <Text style={S.detailRowLabel}>{r.label}</Text>
                      <Text style={S.detailRowValue} numberOfLines={2}>{r.value}</Text>
                    </View>
                  ))}
                </View>

                {/* Capacity */}
                {e.max_attendees > 0 && (
                  <View style={[S.detailCard, { marginTop: 8 }]}>
                    <CapacityBar registered={e.registered_count} max={e.max_attendees} color={color} />
                    {isFull && (
                      <Text style={{ fontSize: 12, color: '#D32F2F', fontWeight: '700', marginTop: 6 }}>
                        ⚠ This event is at full capacity. You can join the waitlist.
                      </Text>
                    )}
                  </View>
                )}

                {/* Description */}
                {e.description ? (
                  <View style={{ marginTop: 8 }}>
                    <SectionTitle title="About this Event" icon="information-circle-outline" />
                    <Text style={{ fontSize: 14, color: '#334155', lineHeight: 22 }}>{e.description}</Text>
                  </View>
                ) : null}

                {/* Requirements */}
                {e.requirements?.length > 0 && (
                  <View style={{ marginTop: 8 }}>
                    <SectionTitle title="Requirements" icon="alert-circle-outline" />
                    {e.requirements.map((r, i) => (
                      <View key={i} style={{ flexDirection: 'row', gap: 8, marginBottom: 7 }}>
                        <Ionicons name="checkmark-circle-outline" size={16} color="#388E3C" />
                        <Text style={{ fontSize: 14, color: '#334155', flex: 1 }}>{r}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Tags */}
                {e.tags?.length > 0 && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
                    {e.tags.map((t, i) => <View key={i} style={S.tag}><Text style={S.tagText}>{t}</Text></View>)}
                  </View>
                )}

                {/* Sponsors */}
                {e.sponsors?.length > 0 && (
                  <View style={{ marginTop: 12 }}>
                    <SectionTitle title="Sponsors" icon="ribbon-outline" />
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                      {e.sponsors.map((s, i) => (
                        <View key={i} style={{ backgroundColor: '#E3F2FD', borderRadius: 10,
                          paddingHorizontal: 12, paddingVertical: 6 }}>
                          <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '600' }}>
                            {typeof s === 'string' ? s : s.name}
                          </Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {/* Social media */}
                {(e.hashtag || e.social_media) && (
                  <View style={{ marginTop: 12 }}>
                    <SectionTitle title="Social Media" icon="share-social-outline" />
                    {e.hashtag && (
                      <Text style={{ fontSize: 14, color: '#1DA1F2', fontWeight: '600' }}>#{e.hashtag.replace('#','')}</Text>
                    )}
                  </View>
                )}
              </View>
            )}

            {/* ── AGENDA ── */}
            {detailTab === 'agenda' && (
              <View>
                {(e.agenda?.length > 0 || e.sessions?.length > 0) ? (
                  <>
                    {(e.agenda?.length > 0 ? e.agenda : e.sessions).map((item, i) => (
                      <View key={i} style={[S.agendaItem, { borderLeftColor: color }]}>
                        <View style={[S.agendaDot, { backgroundColor: color }]} />
                        <View style={{ flex: 1, paddingLeft: 14 }}>
                          <Text style={{ fontSize: 13, fontWeight: '800', color: '#0F172A' }}>
                            {item.activity || item.title}
                          </Text>
                          {item.time && (
                            <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '600', marginTop: 2 }}>
                              {item.time}
                            </Text>
                          )}
                          {item.speaker && (
                            <Text style={{ fontSize: 12, color: '#78909C', marginTop: 2 }}>
                              🎤 {item.speaker}
                            </Text>
                          )}
                          {item.location && (
                            <Text style={{ fontSize: 12, color: '#78909C' }}>📍 {item.location}</Text>
                          )}
                          {item.description && (
                            <Text style={{ fontSize: 12, color: '#546E7A', marginTop: 4, lineHeight: 17 }}>{item.description}</Text>
                          )}
                        </View>
                      </View>
                    ))}

                    {/* Activities list */}
                    {e.activities?.length > 0 && (
                      <View style={{ marginTop: 16 }}>
                        <SectionTitle title="Activities" icon="list-outline" />
                        {e.activities.map((a, i) => (
                          <View key={i} style={{ flexDirection: 'row', gap: 8, marginBottom: 8 }}>
                            <Ionicons name="play-circle-outline" size={16} color={color} />
                            <Text style={{ fontSize: 14, color: '#334155', flex: 1 }}>{a}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </>
                ) : (
                  <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                    <Ionicons name="calendar-outline" size={44} color="#CFD8DC" />
                    <Text style={{ color: '#90A4AE', marginTop: 12 }}>No agenda available</Text>
                  </View>
                )}
              </View>
            )}

            {/* ── SPEAKERS ── */}
            {detailTab === 'speakers' && (
              <View>
                {e.speakers?.length > 0 ? e.speakers.map((sp, i) => (
                  <View key={i} style={S.speakerCard}>
                    <View style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: color,
                      alignItems: 'center', justifyContent: 'center' }}>
                      <Text style={{ color: '#fff', fontSize: 18, fontWeight: '900' }}>
                        {initials(sp.name)}
                      </Text>
                    </View>
                    <View style={{ flex: 1, marginLeft: 14 }}>
                      <Text style={{ fontSize: 15, fontWeight: '800', color: '#0F172A' }}>{sp.name}</Text>
                      {sp.title && <Text style={{ fontSize: 13, color: color, fontWeight: '600' }}>{sp.title}</Text>}
                      {sp.company && <Text style={{ fontSize: 12, color: '#78909C' }}>{sp.company}</Text>}
                      {sp.bio && <Text style={{ fontSize: 13, color: '#546E7A', marginTop: 6, lineHeight: 18 }}>{sp.bio}</Text>}
                    </View>
                  </View>
                )) : (
                  <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                    <Ionicons name="mic-outline" size={44} color="#CFD8DC" />
                    <Text style={{ color: '#90A4AE', marginTop: 12 }}>No speakers listed</Text>
                  </View>
                )}
              </View>
            )}

            {/* ── LOGISTICS ── */}
            {detailTab === 'logistics' && (
              <View>
                {/* Catering */}
                {e.catering && (
                  <View style={{ marginBottom: 16 }}>
                    <SectionTitle title="Catering" icon="restaurant-outline" />
                    <View style={S.detailCard}>
                      {['breakfast','lunch','dinner'].filter(m => e.catering[m]).map(m => (
                        <View key={m} style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
                          <Ionicons name="checkmark-circle" size={16} color="#388E3C" />
                          <Text style={{ fontSize: 14, color: '#334155', textTransform: 'capitalize' }}>{m} provided</Text>
                        </View>
                      ))}
                      {e.catering.dietaryOptions?.length > 0 && (
                        <View style={{ marginTop: 8 }}>
                          <Text style={{ fontSize: 12, color: '#90A4AE', marginBottom: 6, fontWeight: '600' }}>DIETARY OPTIONS</Text>
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                            {e.catering.dietaryOptions.map((d, i) => (
                              <View key={i} style={[S.tag, { backgroundColor: '#E8F5E9', borderColor: '#A5D6A7' }]}>
                                <Text style={[S.tagText, { color: '#2E7D32' }]}>{d}</Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      )}
                    </View>
                  </View>
                )}

                {/* Parking */}
                {e.parking && (
                  <View style={{ marginBottom: 16 }}>
                    <SectionTitle title="Parking" icon="car-outline" />
                    <View style={S.detailCard}>
                      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
                        <Ionicons name={e.parking.available ? 'checkmark-circle' : 'close-circle'} size={16}
                          color={e.parking.available ? '#388E3C' : '#D32F2F'} />
                        <Text style={{ fontSize: 14, color: '#334155' }}>
                          Parking {e.parking.available ? 'available' : 'not available'}
                        </Text>
                      </View>
                      {e.parking.cost && <Text style={{ fontSize: 13, color: '#78909C' }}>Cost: {e.parking.cost}</Text>}
                      {e.parking.instructions && <Text style={{ fontSize: 13, color: '#546E7A', marginTop: 4 }}>{e.parking.instructions}</Text>}
                    </View>
                  </View>
                )}

                {/* Accessibility */}
                {e.accessibility && (
                  <View style={{ marginBottom: 16 }}>
                    <SectionTitle title="Accessibility" icon="accessibility-outline" />
                    <View style={S.detailCard}>
                      {[
                        { key: 'wheelchairAccessible', label: 'Wheelchair Accessible' },
                        { key: 'signLanguage',          label: 'Sign Language' },
                        { key: 'assistiveListening',    label: 'Assistive Listening' },
                      ].map(a => (
                        <View key={a.key} style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
                          <Ionicons name={e.accessibility[a.key] ? 'checkmark-circle' : 'close-circle'} size={16}
                            color={e.accessibility[a.key] ? '#388E3C' : '#BDBDBD'} />
                          <Text style={{ fontSize: 14, color: '#334155' }}>{a.label}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {/* Virtual info */}
                {e.is_virtual && (
                  <View style={{ marginBottom: 16 }}>
                    <SectionTitle title="Virtual Event" icon="videocam-outline" />
                    <View style={S.detailCard}>
                      {e.virtual_link ? (
                        <View>
                          <Text style={{ fontSize: 13, color: '#546E7A', marginBottom: 8 }}>
                            Join via the video conference platform:
                          </Text>
                          <TouchableOpacity style={{ backgroundColor: '#0e4e96', borderRadius: 10,
                            paddingVertical: 11, flexDirection: 'row', alignItems: 'center',
                            justifyContent: 'center', gap: 8 }}
                            onPress={() => navigation?.navigate('BusinessVideoConferenceScreen', {
                              meetingTitle: e.title, autoJoin: false,
                            })}>
                            <Ionicons name="videocam" size={18} color="#fff" />
                            <Text style={{ color: '#fff', fontSize: 14, fontWeight: '700' }}>Join Virtual Event</Text>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <Text style={{ fontSize: 13, color: '#90A4AE' }}>
                          Virtual link will be shared closer to the event date.
                        </Text>
                      )}
                    </View>
                  </View>
                )}

                {/* Materials */}
                {e.materials?.length > 0 && (
                  <View>
                    <SectionTitle title="Materials" icon="documents-outline" />
                    {e.materials.map((m, i) => (
                      <View key={i} style={{ flexDirection: 'row', gap: 8, marginBottom: 8 }}>
                        <Ionicons name="document-outline" size={16} color="#1976D2" />
                        <Text style={{ fontSize: 14, color: '#334155', flex: 1 }}>{m}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Certifications */}
                {e.certifications?.length > 0 && (
                  <View style={{ marginTop: 8 }}>
                    <SectionTitle title="Certifications" icon="ribbon-outline" />
                    {e.certifications.map((c, i) => (
                      <View key={i} style={{ flexDirection: 'row', gap: 8, marginBottom: 8 }}>
                        <Ionicons name="ribbon-outline" size={16} color="#7B1FA2" />
                        <Text style={{ fontSize: 14, color: '#334155', flex: 1 }}>{c}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}

            {/* ── ATTENDEES ── */}
            {detailTab === 'attendees' && (
              <View>
                <View style={[S.detailCard, { marginBottom: 12 }]}>
                  <Text style={{ fontSize: 13, color: '#90A4AE', fontWeight: '700', marginBottom: 8 }}>REGISTRATION SUMMARY</Text>
                  {[
                    { label: 'Registered', value: e.registered_count },
                    { label: 'Capacity',   value: e.max_attendees || '∞' },
                    { label: 'Available',  value: e.max_attendees ? Math.max(0, e.max_attendees - e.registered_count) : '∞' },
                    { label: 'Status',     value: isFull ? 'Full' : 'Open' },
                  ].map(r => (
                    <View key={r.label} style={{ flexDirection: 'row', justifyContent: 'space-between',
                      paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: '#F5F5F5' }}>
                      <Text style={{ fontSize: 13, color: '#78909C' }}>{r.label}</Text>
                      <Text style={{ fontSize: 13, fontWeight: '700', color: '#263238' }}>{r.value}</Text>
                    </View>
                  ))}
                </View>
                <View style={{ alignItems: 'center', paddingVertical: 24 }}>
                  <Ionicons name="people-outline" size={44} color="#CFD8DC" />
                  <Text style={{ color: '#90A4AE', marginTop: 8, fontSize: 13 }}>
                    Detailed attendee list is not publicly visible.
                  </Text>
                </View>
              </View>
            )}
          </ScrollView>

          {/* Footer RSVP */}
          {new Date(e.start_date) > new Date() && (
            <View style={S.mFooter}>
              {myRsvp === 'attending' ? (
                <View style={{ flex: 1, flexDirection: 'row', gap: 10 }}>
                  <View style={{ flex: 1 }}>
                    <View style={{ backgroundColor: '#E8F5E9', borderRadius: 12, paddingVertical: 13,
                      alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6,
                      marginBottom: regCodes[e.id] ? 8 : 0 }}>
                      <Ionicons name="checkmark-circle" size={18} color="#2E7D32" />
                      <Text style={{ color: '#2E7D32', fontWeight: '700', fontSize: 15 }}>You're Going</Text>
                    </View>
                    {regCodes[e.id] && (
                      <View style={{ backgroundColor: '#F3E5F5', borderRadius: 10, padding: 10,
                        alignItems: 'center', borderWidth: 1, borderColor: '#CE93D8' }}>
                        <Text style={{ fontSize: 10, color: '#7B1FA2', fontWeight: '700', letterSpacing: 0.5 }}>
                          REGISTRATION CODE
                        </Text>
                        <Text style={{ fontSize: 18, fontWeight: '900', color: '#4A148C',
                          letterSpacing: 2, marginTop: 2 }}>
                          {regCodes[e.id]}
                        </Text>
                      </View>
                    )}
                  </View>
                  <TouchableOpacity style={{ flex: 1, borderWidth: 1.5, borderColor: '#D32F2F', borderRadius: 12,
                    paddingVertical: 13, alignItems: 'center' }}
                    onPress={() => handleRsvp(e, 'declined')}>
                    <Text style={{ color: '#D32F2F', fontWeight: '700', fontSize: 14 }}>Cancel RSVP</Text>
                  </TouchableOpacity>
                </View>
              ) : e.registration_required ? (
                <TouchableOpacity style={{ flex: 1, backgroundColor: isFull ? '#F5F5F5' : color,
                  borderRadius: 12, paddingVertical: 14, alignItems: 'center',
                  flexDirection: 'row', justifyContent: 'center', gap: 8 }}
                  onPress={() => setShowRsvp(e)} disabled={saving}>
                  {saving
                    ? <ActivityIndicator color="#fff" size={18} />
                    : <>
                        <Ionicons name={isFull ? 'time-outline' : 'calendar-outline'} size={18}
                          color={isFull ? '#9E9E9E' : '#fff'} />
                        <Text style={{ color: isFull ? '#9E9E9E' : '#fff', fontWeight: '800', fontSize: 15 }}>
                          <Text>{isFull ? 'Join Waitlist' : 'Register Now'}</Text>
                        </Text>
                      </>}
                </TouchableOpacity>
              ) : (
                <View style={{ flex: 1, backgroundColor: '#E3F2FD', borderRadius: 12, paddingVertical: 14,
                  alignItems: 'center' }}>
                  <Text style={{ color: '#0e4e96', fontWeight: '700', fontSize: 15 }}>No Registration Required</Text>
                </View>
              )}
            </View>
          )}
        </SafeAreaView>
      </Modal>
    );
  };

  // ── Render: RSVP Modal ──────────────────────────────────────────────────────
  const renderRsvpModal = () => {
    const e = showRsvp;
    if (!e) return null;
    const isFull = e.is_full || (e.max_attendees && e.registered_count >= e.max_attendees);
    return (
      <Modal visible={!!showRsvp} transparent animationType="slide">
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24 }}>
            <Text style={{ fontSize: 17, fontWeight: '900', color: '#0F172A', marginBottom: 4 }}>{e.title}</Text>
            <Text style={{ fontSize: 13, color: '#78909C', marginBottom: 20 }}>
              {fmtDate(e.start_date)} · {fmtTime(e.start_date)} · {e.location_name}
            </Text>
            {e.max_attendees > 0 && <CapacityBar registered={e.registered_count} max={e.max_attendees} />}
            {e.registration_fee > 0 && (
              <View style={{ backgroundColor: '#FFF3E0', borderRadius: 10, padding: 12, marginTop: 12 }}>
                <Text style={{ fontSize: 13, color: '#E65100', fontWeight: '600' }}>
                  Registration fee: {e.currency} {e.registration_fee}
                </Text>
              </View>
            )}
            <View style={{ gap: 10, marginTop: 16 }}>
              {!isFull && (
                <TouchableOpacity style={{ backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14,
                  alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8 }}
                  onPress={() => handleRsvp(e, 'attending')} disabled={saving}>
                  {saving ? <ActivityIndicator color="#fff" size={18} />
                    : <><Ionicons name="checkmark-circle" size={18} color="#fff" />
                       <Text style={{ color: '#fff', fontWeight: '800', fontSize: 15 }}>Yes, I'm Attending</Text></>}
                </TouchableOpacity>
              )}
              {isFull && (
                <TouchableOpacity style={{ backgroundColor: '#F57C00', borderRadius: 12, paddingVertical: 14,
                  alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 8 }}
                  onPress={() => handleRsvp(e, 'waitlist')} disabled={saving}>
                  <Ionicons name="time-outline" size={18} color="#fff" />
                  <Text style={{ color: '#fff', fontWeight: '800', fontSize: 15 }}>Join Waitlist</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={{ borderWidth: 1.5, borderColor: '#CFD8DC', borderRadius: 12,
                paddingVertical: 13, alignItems: 'center' }}
                onPress={() => handleRsvp(e, 'declined')}>
                <Text style={{ color: '#78909C', fontWeight: '700', fontSize: 15 }}>Can't Make It</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ alignItems: 'center', paddingVertical: 10 }}
                onPress={() => setShowRsvp(null)}>
                <Text style={{ color: '#90A4AE', fontSize: 14 }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ── Render: Create Modal ────────────────────────────────────────────────────
  const renderCreateModal = () => (
    <Modal visible={showCreate} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={S.mHeader}>
          <TouchableOpacity onPress={() => setShowCreate(false)}>
            <Text style={{ fontSize: 15, color: '#78909C' }}>Cancel</Text>
          </TouchableOpacity>
          <Text style={S.mHeaderTitle}>New Event</Text>
          <TouchableOpacity onPress={handleCreate} disabled={saving}>
            {saving ? <ActivityIndicator size="small" color="#0e4e96" />
              : <Text style={{ fontSize: 15, color: '#0e4e96', fontWeight: '800' }}>Create</Text>}
          </TouchableOpacity>
        </View>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
            {/* Basic info */}
            <Text style={S.formSection}>BASIC DETAILS</Text>
            <Text style={S.fl}>Title *</Text>
            <TextInput style={S.fi} value={form.title} onChangeText={v => setForm(p => ({...p, title:v}))}
              placeholder="Event title" placeholderTextColor="#BDBDBD" />

            <Text style={S.fl}>Description</Text>
            <TextInput style={[S.fi, { height: 80, textAlignVertical: 'top' }]} value={form.description}
              onChangeText={v => setForm(p => ({...p, description:v}))} placeholder="Describe the event"
              placeholderTextColor="#BDBDBD" multiline />

            {/* Type */}
            <Text style={S.fl}>Event Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 8, paddingVertical: 4, marginBottom: 14 }}>
              {EVENT_TYPES.map(t => (
                <TouchableOpacity key={t} onPress={() => setForm(p => ({...p, event_type: t}))}
                  style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20,
                    backgroundColor: form.event_type === t ? typeColor(t) : '#F5F7FA',
                    borderWidth: 1, borderColor: form.event_type === t ? typeColor(t) : '#ECEFF1' }}>
                  <Text style={{ fontSize: 13, fontWeight: '600',
                    color: form.event_type === t ? '#fff' : '#546E7A' }}>{t}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {/* Location */}
            <Text style={S.formSection}>LOCATION</Text>
            <Text style={S.fl}>Venue Name *</Text>
            <TextInput style={S.fi} value={form.location_name}
              onChangeText={v => setForm(p => ({...p, location_name:v}))}
              placeholder="e.g. London Convention Center" placeholderTextColor="#BDBDBD" />

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>City</Text>
                <TextInput style={S.fi} value={form.location_city}
                  onChangeText={v => setForm(p => ({...p, location_city:v}))}
                  placeholder="London" placeholderTextColor="#BDBDBD" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>Organiser</Text>
                <TextInput style={S.fi} value={form.organizer_name}
                  onChangeText={v => setForm(p => ({...p, organizer_name:v}))}
                  placeholder="Dept / Name" placeholderTextColor="#BDBDBD" />
              </View>
            </View>

            {/* Dates */}
            <Text style={S.formSection}>SCHEDULE</Text>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>Start (YYYY-MM-DD HH:MM)</Text>
                <TextInput style={S.fi} value={form.start_date}
                  onChangeText={v => setForm(p => ({...p, start_date:v}))}
                  placeholder="2026-06-15 09:00" placeholderTextColor="#BDBDBD" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>End</Text>
                <TextInput style={S.fi} value={form.end_date}
                  onChangeText={v => setForm(p => ({...p, end_date:v}))}
                  placeholder="2026-06-15 17:00" placeholderTextColor="#BDBDBD" />
              </View>
            </View>

            {/* Registration */}
            <Text style={S.formSection}>REGISTRATION</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14 }}>
              <View>
                <Text style={S.fl}>Registration Required</Text>
                <Text style={{ fontSize: 11, color: '#90A4AE' }}>Members must RSVP to attend</Text>
              </View>
              <Switch value={form.registration_required}
                onValueChange={v => setForm(p => ({...p, registration_required:v}))}
                trackColor={{ false: '#CFD8DC', true: '#0e4e96' }} thumbColor="#fff" />
            </View>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>Max Attendees</Text>
                <TextInput style={S.fi} value={form.max_attendees}
                  onChangeText={v => setForm(p => ({...p, max_attendees:v}))}
                  placeholder="150" placeholderTextColor="#BDBDBD" keyboardType="numeric" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>Fee (£)</Text>
                <TextInput style={S.fi} value={form.registration_fee}
                  onChangeText={v => setForm(p => ({...p, registration_fee:v}))}
                  placeholder="0" placeholderTextColor="#BDBDBD" keyboardType="decimal-pad" />
              </View>
            </View>

            {form.registration_required && (
              <>
                <Text style={S.fl}>Registration Deadline (YYYY-MM-DD)</Text>
                <TextInput style={S.fi} value={form.registration_deadline}
                  onChangeText={v => setForm(p => ({...p, registration_deadline:v}))}
                  placeholder="2026-06-01" placeholderTextColor="#BDBDBD" />
              </>
            )}

            {/* Visibility & Type */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14 }}>
              <View>
                <Text style={S.fl}>Public Event</Text>
                <Text style={{ fontSize: 11, color: '#90A4AE' }}>Visible to external attendees</Text>
              </View>
              <Switch value={form.is_public}
                onValueChange={v => setForm(p => ({...p, is_public:v}))}
                trackColor={{ false: '#CFD8DC', true: '#0e4e96' }} thumbColor="#fff" />
            </View>

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14 }}>
              <View>
                <Text style={S.fl}>Virtual Event</Text>
                <Text style={{ fontSize: 11, color: '#90A4AE' }}>Held online</Text>
              </View>
              <Switch value={form.is_virtual}
                onValueChange={v => setForm(p => ({...p, is_virtual:v}))}
                trackColor={{ false: '#CFD8DC', true: '#0e4e96' }} thumbColor="#fff" />
            </View>

            {form.is_virtual && (
              <>
                <Text style={S.fl}>Virtual Link</Text>
                <TextInput style={S.fi} value={form.virtual_link}
                  onChangeText={v => setForm(p => ({...p, virtual_link:v}))}
                  placeholder="https://meet.jobeng.ai/..." placeholderTextColor="#BDBDBD" />
              </>
            )}

            {/* Contact */}
            <Text style={S.formSection}>CONTACT</Text>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>Email</Text>
                <TextInput style={S.fi} value={form.contact_email}
                  onChangeText={v => setForm(p => ({...p, contact_email:v}))}
                  placeholder="events@company.com" placeholderTextColor="#BDBDBD" keyboardType="email-address" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={S.fl}>Phone</Text>
                <TextInput style={S.fi} value={form.contact_phone}
                  onChangeText={v => setForm(p => ({...p, contact_phone:v}))}
                  placeholder="+44 20 7946 0958" placeholderTextColor="#BDBDBD" />
              </View>
            </View>

            <Text style={S.fl}>Tags (comma separated)</Text>
            <TextInput style={S.fi} value={form.tags}
              onChangeText={v => setForm(p => ({...p, tags:v}))}
              placeholder="Conference, Annual, Technology" placeholderTextColor="#BDBDBD" />

            <Text style={S.fl}>Hashtag</Text>
            <TextInput style={S.fi} value={form.hashtag}
              onChangeText={v => setForm(p => ({...p, hashtag:v}))}
              placeholder="#EventName2026" placeholderTextColor="#BDBDBD" />

            <View style={{ height: 30 }} />
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ── Render: Stats Modal ─────────────────────────────────────────────────────
  const renderStatsModal = () => (
    <Modal visible={showStats} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={S.mHeader}>
          <TouchableOpacity onPress={() => setShowStats(false)}>
            <Ionicons name="close" size={24} color="#607D8B" />
          </TouchableOpacity>
          <Text style={S.mHeaderTitle}>Event Statistics</Text>
          <View style={{ width: 28 }} />
        </View>
        <ScrollView contentContainerStyle={{ padding: 16 }}>
          <LinearGradient colors={['#0e4e96','#1565C0']} style={{ borderRadius: 18, padding: 20, marginBottom: 16 }}>
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: '600', marginBottom: 4 }}>Total Events</Text>
            <Text style={{ color: '#fff', fontSize: 48, fontWeight: '900' }}>{stats.total}</Text>
            <View style={{ flexDirection: 'row', gap: 20, marginTop: 12 }}>
              {[
                { label: 'Upcoming',   value: stats.upcoming },
                { label: 'My RSVPs',   value: stats.myRsvps },
                { label: 'Avg Capacity', value: `${stats.avgCapacity}%` },
              ].map(s => (
                <View key={s.label}>
                  <Text style={{ color: '#fff', fontSize: 22, fontWeight: '800' }}>{s.value}</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>{s.label}</Text>
                </View>
              ))}
            </View>
          </LinearGradient>

          <Text style={{ fontSize: 13, fontWeight: '800', color: '#90A4AE', textTransform: 'uppercase',
            letterSpacing: 0.7, marginBottom: 12 }}>Events by Type</Text>
          {Object.entries(stats.byType).filter(([,v]) => v > 0).map(([type, count]) => (
            <View key={type} style={{ marginBottom: 10 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: typeColor(type) }} />
                  <Text style={{ fontSize: 13, color: '#37474F', fontWeight: '600' }}>{type}</Text>
                </View>
                <Text style={{ fontSize: 13, fontWeight: '700', color: typeColor(type) }}>{count}</Text>
              </View>
              <View style={{ height: 6, backgroundColor: '#ECEFF1', borderRadius: 3, overflow: 'hidden' }}>
                <View style={{ width: `${Math.round((count / stats.total) * 100)}%`, height: '100%',
                  backgroundColor: typeColor(type), borderRadius: 3 }} />
              </View>
            </View>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Render: Notifications Modal ─────────────────────────────────────────────
  const renderNotifsModal = () => (
    <Modal visible={showNotifs} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F5F7FA' }}>
        <View style={S.mHeader}>
          <TouchableOpacity onPress={() => setShowNotifs(false)}>
            <Ionicons name="close" size={24} color="#607D8B" />
          </TouchableOpacity>
          <Text style={S.mHeaderTitle}>Notifications</Text>
          <TouchableOpacity onPress={markAllRead}>
            <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '600' }}>Mark all read</Text>
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 14 }}>
          {notifications.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 50 }}>
              <Ionicons name="notifications-outline" size={50} color="#CFD8DC" />
              <Text style={{ color: '#90A4AE', marginTop: 12, fontSize: 14 }}>No notifications yet</Text>
            </View>
          ) : notifications.map(n => (
            <View key={n.id} style={[S.notifCard, !n.read && { borderLeftColor: '#0e4e96', borderLeftWidth: 3 }]}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
                <View style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center',
                  backgroundColor: n.type === 'success' ? '#E8F5E9' : n.type === 'reminder' ? '#FFF3E0' : '#E3F2FD' }}>
                  <Ionicons name={n.type === 'success' ? 'checkmark-circle' : n.type === 'reminder' ? 'alarm' : 'information-circle'}
                    size={18} color={n.type === 'success' ? '#388E3C' : n.type === 'reminder' ? '#F57C00' : '#0e4e96'} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A' }}>{n.title}</Text>
                  <Text style={{ fontSize: 13, color: '#546E7A', marginTop: 2 }}>{n.message}</Text>
                  <Text style={{ fontSize: 11, color: '#90A4AE', marginTop: 4 }}>
                    {fmtDate(n.timestamp)}
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Render: Calendar View ───────────────────────────────────────────────────
  const renderCalendar = () => {
    const days = getCalendarDays();
    const today = new Date();
    return (
      <View style={{ flex: 1, backgroundColor: '#fff' }}>
        {/* Month navigation */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#ECEFF1' }}>
          <TouchableOpacity onPress={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y-1); } else setCalMonth(m => m-1); }}
            style={{ padding: 6 }}>
            <Ionicons name="chevron-back" size={22} color="#0e4e96" />
          </TouchableOpacity>
          <Text style={{ fontSize: 17, fontWeight: '800', color: '#0F172A' }}>
            {MONTHS[calMonth]} {calYear}
          </Text>
          <TouchableOpacity onPress={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y+1); } else setCalMonth(m => m+1); }}
            style={{ padding: 6 }}>
            <Ionicons name="chevron-forward" size={22} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* Day headers */}
        <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 8,
          borderBottomWidth: 1, borderBottomColor: '#ECEFF1' }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <Text key={d} style={{ flex: 1, textAlign: 'center', fontSize: 11,
              fontWeight: '700', color: '#90A4AE' }}>{d}</Text>
          ))}
        </View>

        {/* Calendar grid */}
        <ScrollView>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', paddingHorizontal: 8 }}>
            {days.map((day, i) => {
              const dayEvents = eventsOnDay(day);
              const isToday = day && today.getDate() === day && today.getMonth() === calMonth && today.getFullYear() === calYear;
              return (
                <View key={i} style={{ width: `${100/7}%`, aspectRatio: 0.9, padding: 2 }}>
                  {day ? (
                    <TouchableOpacity
                      style={{ flex: 1, borderRadius: 10, padding: 4, alignItems: 'center',
                        backgroundColor: isToday ? '#0e4e96' : dayEvents.length > 0 ? '#E3F2FD' : 'transparent' }}
                      onPress={() => {
                        if (dayEvents.length === 1) { setSelectedEvent(dayEvents[0]); setDetailTab('about'); setShowDetail(true); }
                      }}>
                      <Text style={{ fontSize: 13, fontWeight: isToday ? '900' : '600',
                        color: isToday ? '#fff' : '#0F172A' }}>{day}</Text>
                      {dayEvents.slice(0, 2).map((e, j) => (
                        <View key={j} style={{ width: '100%', height: 4, borderRadius: 2, marginTop: 2,
                          backgroundColor: typeColor(e.event_type) }} />
                      ))}
                      {dayEvents.length > 2 && (
                        <Text style={{ fontSize: 9, color: '#0e4e96', fontWeight: '700' }}>+{dayEvents.length-2}</Text>
                      )}
                    </TouchableOpacity>
                  ) : <View style={{ flex: 1 }} />}
                </View>
              );
            })}
          </View>

          {/* Events this month list */}
          <View style={{ padding: 16 }}>
            <Text style={{ fontSize: 13, fontWeight: '800', color: '#90A4AE', textTransform: 'uppercase',
              letterSpacing: 0.7, marginBottom: 12 }}>Events in {MONTHS[calMonth]}</Text>
            {events.filter(e => {
              const d = new Date(e.start_date);
              return d.getMonth() === calMonth && d.getFullYear() === calYear;
            }).map(e => (
              <TouchableOpacity key={e.id} style={S.calEventRow}
                onPress={() => { setSelectedEvent(e); setDetailTab('about'); setShowDetail(true); }}>
                <View style={{ width: 4, height: '100%', backgroundColor: typeColor(e.event_type),
                  borderRadius: 2, marginRight: 12 }} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A' }}>{e.title}</Text>
                  <Text style={{ fontSize: 12, color: '#78909C', marginTop: 2 }}>
                    {fmtShort(e.start_date)} · {fmtTime(e.start_date)} · {e.location_name}
                  </Text>
                </View>
                <TypeBadge type={e.event_type} small />
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────
  const TABS = ['Upcoming','Past','My Events','Today'];

  return (
    <SafeAreaView style={S.container}>
      {/* Header */}
      <LinearGradient colors={['#0e4e96','#1565C0']} style={S.header}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={S.headerBtn}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>

        {/* Company logo from CompanyProfile */}
        <View style={{ marginRight: 10 }}>
          {companyLogo
            ? <Image
                source={{ uri: companyLogo || undefined }}
                style={{ width: 38, height: 38, borderRadius: 19,
                  borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' }}
                resizeMode="cover"
              />
            : <View style={{ width: 38, height: 38, borderRadius: 19,
                backgroundColor: 'rgba(255,255,255,0.2)', borderWidth: 2,
                borderColor: 'rgba(255,255,255,0.4)',
                alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: '900' }}>
                  {(companyName || 'JE').split(' ').slice(0, 2)
                    .map(w => w[0]?.toUpperCase() || '').join('') || 'JE'}
                </Text>
              </View>}
        </View>

        <View style={{ flex: 1 }}>
          {companyName ? (
            <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11,
              fontWeight: '600', marginBottom: 1 }}>
              {companyName}
            </Text>
          ) : null}
          <Text style={S.headerTitle}>Company Events</Text>
          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
            {stats.upcoming} upcoming · {stats.myRsvps} registered
          </Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {/* Notifications */}
          <TouchableOpacity style={S.headerBtn} onPress={() => setShowNotifs(true)}>
            <Ionicons name="notifications-outline" size={20} color="#fff" />
            {unreadCount > 0 && (
              <View style={{ position: 'absolute', top: -2, right: -2, width: 16, height: 16, borderRadius: 8,
                backgroundColor: '#D32F2F', alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ fontSize: 9, color: '#fff', fontWeight: '800' }}>{unreadCount}</Text>
              </View>
            )}
          </TouchableOpacity>
          {/* Stats */}
          <TouchableOpacity style={S.headerBtn} onPress={() => setShowStats(true)}>
            <Ionicons name="bar-chart-outline" size={20} color="#fff" />
          </TouchableOpacity>
          {/* Calendar toggle */}
          <TouchableOpacity style={S.headerBtn} onPress={() => setShowCalendar(v => !v)}>
            <Ionicons name={showCalendar ? 'list' : 'calendar-outline'} size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {showCalendar ? renderCalendar() : (
        <>
          {/* Search + controls */}
          <View style={S.searchRow}>
            <View style={S.searchBox}>
              <Ionicons name="search" size={17} color="#90A4AE" />
              <TextInput style={S.searchInput} value={searchQuery} onChangeText={setSearchQuery}
                placeholder="Search events, organiser, tag…" placeholderTextColor="#B0BEC5" />
              {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={17} color="#90A4AE" /></TouchableOpacity> : null}
            </View>
            {/* View toggle */}
            <TouchableOpacity onPress={() => setViewMode(v => v === 'list' ? 'grid' : 'list')}
              style={S.iconBtn}>
              <Ionicons name={viewMode === 'list' ? 'grid-outline' : 'list-outline'} size={20} color="#607D8B" />
            </TouchableOpacity>
            {/* Filter */}
            <TouchableOpacity onPress={() => setShowFilters(v => !v)}
              style={[S.iconBtn, (filterType !== 'All' || filterCategory !== 'All') && { backgroundColor: '#E3F2FD' }]}>
              <Ionicons name="options-outline" size={20}
                color={filterType !== 'All' || filterCategory !== 'All' ? '#0e4e96' : '#607D8B'} />
            </TouchableOpacity>
          </View>

          {/* Filter chips */}
          {showFilters && (
            <View style={{ backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1' }}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 7, paddingHorizontal: 14, paddingVertical: 8 }}>
                <TouchableOpacity onPress={() => setFilterType('All')}
                  style={[S.chip, filterType === 'All' && S.chipActive]}>
                  <Text style={[S.chipText, filterType === 'All' && S.chipTextActive]}>All Types</Text>
                </TouchableOpacity>
                {EVENT_TYPES.map(t => (
                  <TouchableOpacity key={t} onPress={() => setFilterType(filterType === t ? 'All' : t)}
                    style={[S.chip, filterType === t && { backgroundColor: typeColor(t), borderColor: typeColor(t) }]}>
                    <Text style={[S.chipText, filterType === t && { color: '#fff' }]}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              {/* Sort row */}
              <View style={{ flexDirection: 'row', paddingHorizontal: 14, paddingBottom: 8, gap: 8 }}>
                {[['date','Date'],['title','Title'],['attendees','Attendees']].map(([k, l]) => (
                  <TouchableOpacity key={k} onPress={() => { if (sortBy === k) setSortOrder(o => o==='asc'?'desc':'asc'); else setSortBy(k); }}
                    style={[S.chip, sortBy === k && S.chipActive]}>
                    <Text style={[S.chipText, sortBy === k && S.chipTextActive]}>
                      {l} {sortBy === k ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {/* Tabs */}
          <View style={S.tabBar}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
              {TABS.map(t => (
                <TouchableOpacity key={t} style={[S.tab, activeTab === t && S.tabActive]}
                  onPress={() => setActiveTab(t)}>
                  <Text style={[S.tabText, activeTab === t && S.tabTextActive]}>{t}</Text>
                  {t === 'My Events' && stats.myRsvps > 0 && (
                    <View style={{ backgroundColor: '#0e4e96', borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1, marginLeft: 5 }}>
                      <Text style={{ fontSize: 10, color: '#fff', fontWeight: '700' }}>{stats.myRsvps}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Count */}
          <View style={{ paddingHorizontal: 16, paddingVertical: 6, backgroundColor: '#F5F7FA',
            flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 12, color: '#90A4AE' }}>
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
              {(searchQuery || filterType !== 'All') ? ' (filtered)' : ''}
            </Text>
            {(searchQuery || filterType !== 'All') && (
              <TouchableOpacity onPress={() => { setSearchQuery(''); setFilterType('All'); }}>
                <Text style={{ fontSize: 12, color: '#D32F2F', fontWeight: '600' }}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          {loading ? (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
              <ActivityIndicator size="large" color="#0e4e96" />
              <Text style={{ color: '#78909C', marginTop: 12, fontSize: 14 }}>Loading events…</Text>
            </View>
          ) : (
            <FlatList
              data={filteredEvents}
              keyExtractor={e => String(e.id)}
              renderItem={renderEventCard}
              numColumns={viewMode === 'grid' ? 2 : 1}
              key={viewMode}
              contentContainerStyle={{ padding: 14, paddingBottom: 100 }}
              showsVerticalScrollIndicator={false}
              refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
              ListEmptyComponent={
                <View style={{ alignItems: 'center', paddingVertical: 60 }}>
                  <Ionicons name="calendar-outline" size={56} color="#CFD8DC" />
                  <Text style={{ fontSize: 17, fontWeight: '700', color: '#546E7A', marginTop: 16 }}>No events found</Text>
                  <Text style={{ fontSize: 13, color: '#90A4AE', marginTop: 6, textAlign: 'center' }}>
                    <Text>{activeTab === 'My Events' ? 'RSVP to events to see them here' : 'Try adjusting your search or filters'}</Text>
                  </Text>
                </View>
              }
            />
          )}
        </>
      )}

      {/* FAB */}
      <TouchableOpacity style={S.fab} onPress={() => setShowCreate(true)}>
        <Ionicons name="add" size={26} color="#fff" />
      </TouchableOpacity>

      {/* Modals */}
      {renderDetailModal()}
      {renderRsvpModal()}
      {renderCreateModal()}
      {renderStatsModal()}
      {renderNotifsModal()}
    </SafeAreaView>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = StyleSheet.create({
  container:    { flex: 1, backgroundColor: '#F5F7FA' },
  header:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, gap: 10 },
  headerTitle:  { color: '#fff', fontSize: 17, fontWeight: '900' },
  headerBtn:    { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center', justifyContent: 'center' },

  searchRow:    { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', gap: 8 },
  searchBox:    { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F7FA',
    borderRadius: 12, paddingHorizontal: 12, paddingVertical: 9, gap: 8 },
  searchInput:  { flex: 1, fontSize: 14, color: '#263238' },
  iconBtn:      { width: 36, height: 36, borderRadius: 18, backgroundColor: '#F5F7FA',
    alignItems: 'center', justifyContent: 'center' },

  tabBar:       { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#ECEFF1', paddingVertical: 4 },
  tab:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, marginHorizontal: 2, borderRadius: 20 },
  tabActive:    { backgroundColor: '#E3F2FD' },
  tabText:      { fontSize: 13, color: '#90A4AE', fontWeight: '600' },
  tabTextActive:{ color: '#0e4e96', fontWeight: '800' },

  // Event cards
  eventCard:    { backgroundColor: '#fff', borderRadius: 18, marginBottom: 12, overflow: 'hidden',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 8, elevation: 3 },
  cardBanner:   { width: '100%', height: 130, resizeMode: 'cover' },
  cardBannerGrad: { height: 80, alignItems: 'flex-end', justifyContent: 'center', paddingRight: 16 },
  cardBody:     { padding: 14 },
  cardTitle:    { fontSize: 17, fontWeight: '800', color: '#0F172A', marginTop: 4 },
  cardMeta:     { flexDirection: 'row', alignItems: 'center', gap: 5 },
  cardMetaText: { fontSize: 12, color: '#78909C', flex: 1 },
  cardFooter:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F5F5F5' },

  gridCard:     { flex: 1, margin: 5, backgroundColor: '#fff', borderRadius: 14, padding: 12,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 5, elevation: 2 },
  gridTitle:    { fontSize: 13, fontWeight: '700', color: '#0F172A', marginTop: 5 },

  tag:          { backgroundColor: '#F1F5F9', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3,
    borderWidth: 1, borderColor: '#E2E8F0' },
  tagText:      { fontSize: 11, color: '#64748B', fontWeight: '600' },

  rsvpBtn:      { borderRadius: 10, paddingHorizontal: 14, paddingVertical: 7 },
  rsvpBtnText:  { fontSize: 13, fontWeight: '700' },

  // Modals
  mHeader:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },
  mHeaderTitle: { flex: 1, fontSize: 16, fontWeight: '800', color: '#0F172A', textAlign: 'center', marginHorizontal: 8 },
  mFooter:      { flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#ECEFF1',
    padding: 16, backgroundColor: '#fff' },

  infoStrip:    { flexDirection: 'row', backgroundColor: '#fff', paddingHorizontal: 16,
    paddingVertical: 10, gap: 0, borderBottomWidth: 1, borderBottomColor: '#ECEFF1' },
  infoStripItem:{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 5 },
  infoStripText:{ fontSize: 12, color: '#546E7A', fontWeight: '600', flex: 1 },

  dTab:         { paddingHorizontal: 14, paddingVertical: 10, marginHorizontal: 2 },
  dTabText:     { fontSize: 13, color: '#90A4AE', fontWeight: '600', textTransform: 'capitalize' },

  detailCard:   { backgroundColor: '#F8FAFC', borderRadius: 14, padding: 14, marginBottom: 4 },
  detailRow:    { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#F0F4F8' },
  detailRowIcon:{ width: 28, height: 28, borderRadius: 14, backgroundColor: '#E3F2FD',
    alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0 },
  detailRowLabel:{ width: 95, fontSize: 12, color: '#90A4AE', fontWeight: '600' },
  detailRowValue:{ flex: 1, fontSize: 13, color: '#37474F', fontWeight: '500' },

  agendaItem:   { flexDirection: 'row', borderLeftWidth: 2, marginLeft: 8,
    paddingBottom: 16, paddingLeft: 0, position: 'relative' },
  agendaDot:    { width: 12, height: 12, borderRadius: 6, position: 'absolute', left: -7, top: 2 },

  speakerCard:  { flexDirection: 'row', backgroundColor: '#F8FAFC', borderRadius: 14, padding: 14, marginBottom: 10 },

  calEventRow:  { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12,
    padding: 12, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },

  notifCard:    { backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },

  chip:         { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20,
    backgroundColor: '#F5F7FA', borderWidth: 1, borderColor: '#ECEFF1' },
  chipActive:   { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  chipText:     { fontSize: 12, color: '#546E7A', fontWeight: '600' },
  chipTextActive:{ color: '#fff' },

  fab:          { position: 'absolute', bottom: 24, right: 24, width: 56, height: 56, borderRadius: 28,
    backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center',
    shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 10, elevation: 8 },

  // Create form
  formSection:  { fontSize: 11, fontWeight: '800', color: '#0e4e96', textTransform: 'uppercase',
    letterSpacing: 1, marginTop: 18, marginBottom: 12 },
  fl:           { fontSize: 13, fontWeight: '600', color: '#546E7A', marginBottom: 5 },
  fi:           { backgroundColor: '#fff', borderWidth: 1.5, borderColor: '#CFD8DC', borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#263238', marginBottom: 14 },
});